import { Wire, Connector, HarnessNode, WireBundle, WireBundleSegment, WireStub, Segment } from '../types';
import { BUNDLE_GEOMETRY, COLORS } from '../utils/constants';
import { Point, connectorCenter } from '../utils/geometry';
import { connectorAnchor, connectorBreakout, pinEdgeAnchor } from '../utils/wireGeometry';
import { pinWireCounts, PinWireCounts } from '../utils/connectorLayout';
import { segmentId } from './StateManager';

/** Fallback colour for the summarized trunk and any wire missing an explicit colour. */
const TRUNK_COLOR = COLORS.WIRE_BUNDLE;

/** A same-connector jumper with no waypoints, drawn as a bow rather than a section. */
function isJumper(wire: Wire): boolean {
  return wire.startConnectorId === wire.endConnectorId && wire.path.length === 0;
}

/** The pin a wire terminates on at a given vertex, or undefined for a mid-route node. */
function wirePinAt(wire: Wire, vertexId: string): string | undefined {
  if (wire.startConnectorId === vertexId) {
    return wire.startPinId;
  }
  if (wire.endConnectorId === vertexId) {
    return wire.endPinId;
  }
  return undefined;
}

/** One end of a section: where the trunk meets it and the fanned wire stubs there. */
interface SectionEnd {
  trunkPoint: Point;
  stubs: WireStub[];
}

/**
 * Builds the drawable wire bundles from the harness. Through-wires are grouped by
 * the physical section (the pair of adjacent vertices) they traverse; each shared
 * section is drawn once as a summarized trunk, with the individual wires fanning
 * out to their pin rows near each connector. Same-connector jumpers are drawn as
 * bows between their two pins.
 */
export class BundleManager {
  private bundles: Map<string, WireBundle> = new Map();

  updateBundles(
    wires: Wire[],
    connectors: Connector[],
    nodes: HarnessNode[],
    segments: Segment[] = []
  ): void {
    this.bundles.clear();

    const connectorById = new Map(connectors.map(c => [c.id, c]));
    const positionById = new Map<string, Point>();
    connectors.forEach(c => positionById.set(c.id, connectorCenter(c)));
    nodes.forEach(n => positionById.set(n.id, { x: n.xPosition, y: n.yPosition }));
    const lengthById = new Map(segments.map(s => [s.id, s.length]));
    // Per-connector pin wire counts, so wire anchors match the expanded pin rows.
    const countsById = new Map(connectors.map(c => [c.id, pinWireCounts(c.id, wires)]));

    const sections = this.groupWiresBySection(wires.filter(w => !isJumper(w)));
    sections.forEach((entry, id) => {
      const bundle = this.buildSectionBundle(id, entry, connectorById, positionById, lengthById, countsById);
      if (bundle) {
        this.bundles.set(id, bundle);
      }
    });

    wires.filter(isJumper).forEach(wire => {
      const bundle = this.buildJumperBundle(wire, connectorById, countsById);
      if (bundle) {
        this.bundles.set(bundle.id, bundle);
      }
    });
  }

  /** Groups wires by the section id of each adjacent vertex pair along their route. */
  private groupWiresBySection(wires: Wire[]): Map<string, { aId: string; bId: string; wires: Wire[] }> {
    const sections = new Map<string, { aId: string; bId: string; wires: Wire[] }>();

    wires.forEach(wire => {
      const route = [wire.startConnectorId, ...wire.path, wire.endConnectorId];
      for (let i = 0; i < route.length - 1; i++) {
        const aId = route[i];
        const bId = route[i + 1];
        const id = segmentId(aId, bId);
        let entry = sections.get(id);
        if (!entry) {
          entry = { aId, bId, wires: [] };
          sections.set(id, entry);
        }
        entry.wires.push(wire);
      }
    });

    return sections;
  }

  private buildSectionBundle(
    id: string,
    entry: { aId: string; bId: string; wires: Wire[] },
    connectorById: Map<string, Connector>,
    positionById: Map<string, Point>,
    lengthById: Map<string, number>,
    countsById: Map<string, PinWireCounts>
  ): WireBundle | null {
    const refA = positionById.get(entry.aId);
    const refB = positionById.get(entry.bId);
    if (!refA || !refB) {
      return null;
    }

    const single = entry.wires.length === 1;
    const endA = this.buildEnd(entry.aId, refA, refB, entry.wires, connectorById, single, countsById);
    const endB = this.buildEnd(entry.bId, refB, refA, entry.wires, connectorById, single, countsById);

    const segment: WireBundleSegment = {
      startPoint: endA.trunkPoint,
      endPoint: endB.trunkPoint,
      wireCount: entry.wires.length,
      aId: entry.aId,
      bId: entry.bId,
      length: lengthById.get(id) ?? 0,
      color: single ? (entry.wires[0].color ?? TRUNK_COLOR) : undefined,
      stubs: [...endA.stubs, ...endB.stubs]
    };

    return { id, wires: entry.wires, segments: [segment] };
  }

  /**
   * Resolves one end of a section. A node end sits at the node itself; a
   * connector end either anchors the single wire directly at its pin row, or
   * fans every wire from its pin row into a breakout point that starts the trunk.
   */
  private buildEnd(
    vertexId: string,
    selfRef: Point,
    otherRef: Point,
    wires: Wire[],
    connectorById: Map<string, Connector>,
    single: boolean,
    countsById: Map<string, PinWireCounts>
  ): SectionEnd {
    const connector = connectorById.get(vertexId);
    if (!connector) {
      return { trunkPoint: selfRef, stubs: [] };
    }
    const counts = countsById.get(vertexId) ?? {};

    if (single) {
      return { trunkPoint: connectorAnchor(connector, wirePinAt(wires[0], vertexId), otherRef, counts), stubs: [] };
    }

    const breakout = connectorBreakout(connector, otherRef, BUNDLE_GEOMETRY.BREAKOUT_GAP);
    const stubs = wires.map(wire => ({
      from: connectorAnchor(connector, wirePinAt(wire, vertexId), otherRef, counts),
      to: breakout,
      color: wire.color ?? TRUNK_COLOR,
      wireId: wire.id
    }));
    return { trunkPoint: breakout, stubs };
  }

  private buildJumperBundle(wire: Wire, connectorById: Map<string, Connector>, countsById: Map<string, PinWireCounts>): WireBundle | null {
    const connector = connectorById.get(wire.startConnectorId);
    if (!connector) {
      return null;
    }
    const counts = countsById.get(connector.id) ?? {};
    const bow = {
      from: pinEdgeAnchor(connector, wire.startPinId, counts),
      to: pinEdgeAnchor(connector, wire.endPinId, counts),
      color: wire.color ?? TRUNK_COLOR
    };
    return { id: wire.id, wires: [wire], segments: [], bows: [bow] };
  }

  getBundles(): WireBundle[] {
    return Array.from(this.bundles.values());
  }

}
